import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetCountryLookUpType,
  GetYearlyInvoiceAuditType,
  InvoiceReportDetailsListState,
  InvoiceReportProps,
  MopnthlyInvoiceAuditProps,
  MonthlyResults,
  result,
  GetMonthlyInvoiceAuditType,
  GetAuditList,
  invoicesSummary,
} from '../../../types/Invoicing/InvoiceReport/yearlyInvoiceAudittype'
import invoicesReportListApi from '../../../middleware/api/Invoicing/InvoicesReport/invoicesReportListApi'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const getInvoicesYearlyReportList = createAsyncThunk(
  'invoice/getInvoicesYearlyReportList',
  async (queryParams: InvoiceReportProps, thunkApi) => {
    try {
      return await invoicesReportListApi.getInvoicesYearlyReportList(
        queryParams,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCountryLookup = createAsyncThunk(
  'invoicing/getCountryLookup',
  async (_, thunkApi) => {
    try {
      return await invoicesReportListApi.getCountryLookupList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getMonthlyInvoiceAudit = createAsyncThunk(
  'invoicing/getMonthlyInvoiceAudit',
  async (props: MopnthlyInvoiceAuditProps, thunkApi) => {
    try {
      return await invoicesReportListApi.getMonthlyInvoiceAudit(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceSummary = createAsyncThunk(
  'invoiceClientReport/getInvoiceSummary',
  async (invoiceId: number, thunkApi) => {
    try {
      return await invoicesReportListApi.getInvoiceSummary(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const statusValue = 'SENT'

export const initialInvoiceReportListState: InvoiceReportDetailsListState = {
  getInvoiceReportList: {} as GetYearlyInvoiceAuditType,
  getCountryLookUpType: [],
  getMonthlyReportList: {} as GetMonthlyInvoiceAuditType,
  results: [],
  result: [],
  isLoading: ApiLoadingState.idle,
  SelectValue: '',
  SelectInvoiceType: '',
  StatusValue: statusValue,
  SelectCountry: '',
  customFromValue: '',
  customToValue: '',
  multiText: '',
  auditDto: {} as GetAuditList,
  inVoiceSummary: {} as invoicesSummary,
  checkedItems: ['SENT'],
}
const invoiceReportListSlice = createSlice({
  name: 'invoice List',
  initialState: initialInvoiceReportListState,
  reducers: {
    setSelectValue: (state, action) => {
      state.SelectValue = action.payload
    },
    setSelectedInvoiceValue: (state, action) => {
      state.SelectInvoiceType = action.payload
    },
    setInvoiceStatusValue: (state, action) => {
      state.StatusValue = action.payload
    },
    setSelectInvoiceCountry: (state, action) => {
      state.SelectCountry = action.payload
    },
    setCustomInvoiceFromValue: (state, action) => {
      state.customFromValue = action.payload
    },
    setCustomInvoiceToValue: (state, action) => {
      state.customToValue = action.payload
    },
    setCheckedItems: (state, action) => {
      state.checkedItems = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInvoicesYearlyReportList.fulfilled, (state, action) => {
      state.getInvoiceReportList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(getCountryLookup.fulfilled, (state, action) => {
      state.getCountryLookUpType = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(getMonthlyInvoiceAudit.fulfilled, (state, action) => {
      state.getMonthlyReportList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(getInvoiceSummary.fulfilled, (state, action) => {
      state.inVoiceSummary = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(
      isAnyOf(
        getInvoicesYearlyReportList.pending,
        getCountryLookup.pending,
        getMonthlyInvoiceAudit.pending,
        getInvoiceSummary.pending,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.loading
      },
    )
  },
})
const invoiceReportListThunk = {
  getInvoicesYearlyReportList,
  getCountryLookup,
  getMonthlyInvoiceAudit,
  getInvoiceSummary,
}
const isLoading = (state: RootState): LoadingState =>
  state.invoicesReport.isLoading

const invoiceReportList = (state: RootState): GetYearlyInvoiceAuditType =>
  state.invoicesReport.getInvoiceReportList

const countries = (state: RootState): GetCountryLookUpType[] =>
  state.invoicesReport.getCountryLookUpType

const getSelectValue = (state: RootState): string =>
  state.invoicesReport.SelectValue

const getInvoiceValue = (state: RootState): string =>
  state.invoicesReport.SelectInvoiceType

const getStatusValue = (state: RootState): string =>
  state.invoicesReport.StatusValue

const getCountryValue = (state: RootState): string =>
  state.invoicesReport.SelectCountry

const getCustomFromValue = (state: RootState): string =>
  state.invoicesReport.customFromValue

const getCustomToValue = (state: RootState): string =>
  state.invoicesReport.customToValue

const getSearchInputValue = (state: RootState): string =>
  state.invoicesReport.multiText

const getResult = (state: RootState): result[] => state.invoicesReport.result
const getCheckedItems = (state: RootState): string[] =>
  state.invoicesReport.checkedItems
const invoiceMonthlyReportList = (
  state: RootState,
): GetMonthlyInvoiceAuditType => state.invoicesReport.getMonthlyReportList

const getResults = (state: RootState): MonthlyResults[] =>
  state.invoicesReport.results

const getAuditDto = (state: RootState): GetAuditList =>
  state.invoicesReport.auditDto

const getInVoiceSummary = (state: RootState): invoicesSummary =>
  state.invoicesReport.inVoiceSummary

const invoiceListSelectors = {
  isLoading,
  invoiceReportList,
  countries,
  getInvoiceValue,
  getStatusValue,
  getCountryValue,
  getSelectValue,
  getCustomFromValue,
  getCustomToValue,
  getSearchInputValue,
  getResult,
  invoiceMonthlyReportList,
  getResults,
  getAuditDto,
  getInVoiceSummary,
  getCheckedItems,
}
export const invoicesReportService = {
  ...invoiceReportListThunk,
  actions: invoiceReportListSlice.actions,
  selectors: invoiceListSelectors,
}
export default invoiceReportListSlice.reducer
